// DNA CONNECT - Documentos
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  FileText, Download, CheckCircle, AlertCircle, Loader2,
  File, FileImage, FileSpreadsheet, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const CATEGORIES = {
  contrato: { label: 'Contratos', icon: FileText },
  circular: { label: 'Circulares', icon: File },
  material: { label: 'Materiais', icon: FileSpreadsheet },
  arquivo: { label: 'Arquivos', icon: File },
};
import { DocumentosParaAssinar } from './DocumentosParaAssinar';


export default function DnaConnectDocumentos() {
  const [loading, setLoading] = useState(true);
  const [documentos, setDocumentos] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [acceptDialog, setAcceptDialog] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptChecked, setAcceptChecked] = useState(false);

  useEffect(() => {
    loadDocumentos();
  }, []);

  const loadDocumentos = async () => {
    try {
      const token = localStorage.getItem('familyToken');
      const response = await fetch(`${API_URL}/api/dnaconnect/documentos?token=${token}`);
      const data = await response.json();
      setDocumentos(data);
    } catch (error) {
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!acceptDialog || !acceptChecked) return;
    
    setAccepting(true);
    try {
      const token = localStorage.getItem('familyToken');
      await fetch(`${API_URL}/api/dnaconnect/documentos/${acceptDialog.id}/accept?token=${token}`, {
        method: 'POST'
      });
      
      toast.success('Documento aceito com sucesso!');
      setDocumentos(prev => prev.map(d => 
        d.id === acceptDialog.id ? { ...d, accepted: true } : d
      ));
      setAcceptDialog(null);
      setAcceptChecked(false);
    } catch (error) {
      toast.error('Erro ao aceitar documento');
    } finally {
      setAccepting(false);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) return FileImage;
    if (fileType?.includes('sheet') || fileType?.includes('excel')) return FileSpreadsheet;
    return FileText;
  };

  const filteredDocs = documentos.filter(d => {
    if (selectedCategory === 'all') return true;
    return d.category === selectedCategory;
  });

  const pendingAcceptance = documentos.filter(d => d.require_acceptance && !d.accepted);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Documentos</h1>

      {/* DNA SIGN · assinatura eletrônica */}
      <DocumentosParaAssinar />

      {/* Alerta de Documentos Pendentes */}
      {pendingAcceptance.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-medium text-amber-800">
                  {pendingAcceptance.length} documento(s) aguardando aceite
                </p>
                <p className="text-sm text-amber-600">
                  Por favor, leia e aceite os documentos pendentes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList>
          <TabsTrigger value="all">Todos</TabsTrigger>
          {Object.entries(CATEGORIES).map(([key, { label }]) => (
            <TabsTrigger key={key} value={key}>{label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Lista de Documentos */}
      <div className="grid gap-4">
        {filteredDocs.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum documento encontrado</p>
            </CardContent>
          </Card>
        ) : (
          filteredDocs.map((doc) => {
            const FileIcon = getFileIcon(doc.file_type);
            const category = CATEGORIES[doc.category] || CATEGORIES.arquivo;
            
            return (
              <Card key={doc.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileIcon className="w-7 h-7 text-purple-600" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 truncate">{doc.title}</h3>
                        <Badge variant="secondary">{category.label}</Badge>
                      </div>
                      
                      {doc.description && (
                        <p className="text-sm text-gray-600 line-clamp-1">{doc.description}</p>
                      )}
                      
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(doc.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                        </span>
                        
                        {doc.require_acceptance && (
                          doc.accepted ? (
                            <Badge className="bg-green-100 text-green-700 gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Aceito
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Requer Aceite
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {doc.require_acceptance && !doc.accepted ? (
                        <Button 
                          variant="default"
                          size="sm"
                          onClick={() => setAcceptDialog(doc)}
                        >
                          Ler e Aceitar
                        </Button>
                      ) : (
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1">
                            <Download className="w-4 h-4" />
                            Baixar
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Dialog de Aceite */}
      <Dialog open={!!acceptDialog} onOpenChange={() => {
        setAcceptDialog(null);
        setAcceptChecked(false);
      }}>
        <DialogContent className="max-w-2xl">
          {acceptDialog && (
            <>
              <DialogHeader>
                <DialogTitle>{acceptDialog.title}</DialogTitle>
              </DialogHeader>
              
              <div className="py-4">
                <p className="text-gray-600 mb-4">{acceptDialog.description}</p>
                
                <div className="p-4 bg-gray-50 rounded-lg border mb-4">
                  <p className="text-sm text-gray-500 mb-2">Visualize o documento completo:</p>
                  <a 
                    href={acceptDialog.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-purple-600 hover:underline"
                  >
                    <FileText className="w-4 h-4" />
                    Abrir documento
                  </a>
                </div>
                
                <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <Checkbox
                    id="accept"
                    checked={acceptChecked}
                    onCheckedChange={setAcceptChecked}
                  />
                  <label htmlFor="accept" className="text-sm text-amber-800 cursor-pointer">
                    Declaro que li e concordo com os termos do documento acima.
                    Este aceite tem valor legal e será registrado.
                  </label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setAcceptDialog(null)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleAccept}
                  disabled={!acceptChecked || accepting}
                >
                  {accepting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Confirmar Aceite
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
